import React, { Component, type ErrorInfo, type ReactNode } from 'react';

// Top-level safety net for uncaught render errors. Without this, a single
// throwing component blanks the whole SPA with no message — the user has no
// idea what happened. When wired to Sentry / Honeybadger later, the `report`
// hook below is the one place to call out to it.

interface Props { children: ReactNode; }
interface State { hasError: boolean; error?: Error; }

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('App crashed:', error, info);
    // Sentry wire-up point. Once @sentry/react is installed and initialized
    // in index.tsx, uncomment the next line — Sentry's React integration will
    // tag the event with the component stack from `info.componentStack`.
    //   Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
    // See DEVELOPMENT.md → "Error tracking (Sentry)" for the install steps.
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: 'var(--paper-2, #efe7d7)',
          fontFamily: 'Inter, system-ui, sans-serif',
          color: 'var(--ink, #1e1b16)',
        }}
      >
        <div
          className="sk-box shadow"
          style={{
            maxWidth: 480,
            padding: 24,
            background: 'var(--paper, #faf6ef)',
          }}
        >
          <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>something broke</h2>
          <p style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink-soft, #5a5246)' }}>
            study buddy ran into an unexpected error. nothing was lost —
            reloading the page usually fixes it. if it keeps happening, please
            let us know.
          </p>
          <button
            className="sk-btn primary"
            onClick={this.handleReload}
            style={{ marginTop: 12 }}
          >
            reload page
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
